import { atom, useAtom } from "jotai";
import { Permission, SubPermissionsObj } from "src/api/types";
import {
  checkHasAuthToken,
  getAuthToken,
  removeAuthToken,
  setAuthToken,
  getAuthUserInfo,
  setAuthUserInfo,
  removeAuthUserInfo,
} from "../api/token-util";

export type PermissionAction = keyof SubPermissionsObj;

const extractSubPermission = (perm: any, key: PermissionAction): boolean => {
  if (!perm) return false;
  const subsObj = perm.subPermissions || perm.sub_permissions || perm;
  if (subsObj && (subsObj[key] === true || subsObj[key] === 1 || subsObj[key] === "true")) {
    return true;
  }
  if (Array.isArray(perm.actions)) {
    const rawKey = key.replace(/^can_/, "").toLowerCase();
    return perm.actions.map((a: any) => String(a).toLowerCase().trim()).includes(rawKey);
  }
  return false;
};

const authorizationAtom = atom(checkHasAuthToken());
export default function useAuth() {
  const [isAuthorized, setAuthorized] = useAtom(authorizationAtom);

  const getModulePermission = (name: string | string[]): Permission | undefined => {
    const userInfo = getAuthUserInfo();
    const permissions: Permission[] =
      userInfo?.permissions || userInfo?.role?.permissions || [];
    if (!name || !Array.isArray(permissions)) return undefined;
    const names = Array.isArray(name) ? name : [name];
    for (const n of names) {
      if (!n) continue;
      const target = n.toLowerCase().trim();
      const found = permissions.find((p: any) => {
        const pName = (p?.name || p?.permission_name || p?.permission_key)
          ?.toLowerCase()
          ?.trim();
        return pName === target;
      });
      if (found) return found;
    }
    return undefined;
  };

  const hasPermission = (name: string | string[], action?: PermissionAction): boolean => {
    const perm = getModulePermission(name);
    if (!perm) return false;
    if (!action) return true;
    return extractSubPermission(perm, action);
  };

  const checkPermission = (names: string[]) => {
    const userInfo = getAuthUserInfo();
    const permissions: Permission[] =
      userInfo?.permissions || userInfo?.role?.permissions || [];
    const permissionNames = permissions?.map(
      (item: any) =>
        (item.name || item.permission_name || item.permission_key)
          ?.toLowerCase()
          ?.trim()
    );
    return names.some((item) =>
      permissionNames?.includes(item?.toLowerCase()?.trim())
    );
  };

  return {
    setToken: setAuthToken,
    getToken: getAuthToken,
    getCurrrentUser: getAuthUserInfo,
    setCurrentUser: setAuthUserInfo,
    isAuthorized,
    authorize(token: string) {
      setAuthToken(token);
      setAuthorized(true);
    },
    unauthorize() {
      setAuthorized(false);
      removeAuthUserInfo();
      removeAuthToken();
    },
    checkPermission,
    hasPermission,
    getModulePermission,
  };
}

